import useCodeSnippet from "./useCodeSnippet";
import IconTypes from "../../components/Configurator/Input/IconInput/IconTypes";
import {MessagePopupCore} from "@dts-soldel/dts-react-common";
import ICON_TYPES from "../../../../../src/js/misc/IconTypes";
import {utahHeaderLoaded} from "../../components/UtahHeader/UtahHeader";
import UtahIDTypes from "../../components/Configurator/UtahId/UtahIDTypes";
import createMenuConfig from "./createMenuConfig";
import ENVIRONMENT from "../../../../../src/js/misc/environment";

const setIcon = (iconName, configuratorStore, config) => {
	/**
	 * @param {object} appGridIcon - Configuration for the appGridIcon action item
	 * 		@param {string} appGridIcon.title - Title of the app grid icon action item
	 * 		@param {object} appGridIcon.menu - A menu for the app grid icon action item
	 * 		@param {string} appGridIcon.menuStyle - A menu style for the app grid icon action item - See POPUP_MENU_STYLE enum
	 * 		@param {function} appGridIcon.onClick - A function to call when the user clicks the app grid icon action item
	 * 		@param {ChildNode} appGridIcon.popupContent - Arbitrary Dom content to display in a popup panel when the user clicks this action item
	 */

	if (configuratorStore[`${iconName}IconType`] !== IconTypes.none) {
		config[`${iconName}Icon`] = {
			menuStyle: configuratorStore[`${iconName}IconMenuStyle`],
			onHover: configuratorStore[`${iconName}IconOnHover`] ? useCodeSnippet(configuratorStore[`${iconName}IconOnHover`]) : undefined,
			showTitle: configuratorStore[`${iconName}IconShowTitle`],
			title: configuratorStore[`${iconName}IconTitle`],
		};
	}

	switch (configuratorStore[`${iconName}IconType`]) {
		case IconTypes.none:
			break;

		case IconTypes.onClick:
			config[`${iconName}Icon`].onClick = configuratorStore[`${iconName}IconOnClick`] ? useCodeSnippet(configuratorStore[`${iconName}IconOnClick`]) : undefined;
			break;

		case IconTypes.popupContent:
			config[`${iconName}Icon`].popupContent = utahHeader && utahHeader.renderDOM(configuratorStore[`${iconName}IconPopupContent`]);
			break;

		case IconTypes.menu:
			config[`${iconName}Icon`].menu = createMenuConfig(configuratorStore[`${iconName}IconMenu`]);
			break;

		default:
			MessagePopupCore.addMessage({
				title: 'UnknownIconType',
				message: `An unknown ${iconName}IconType has been selected '${configuratorStore[`${iconName}IconType`]}'! Please contact a Utah Header developer`,
			});
			break;
	}
};

const setHeaderLogo = (configuratorStore, config) => {
	config.headerLogoType = configuratorStore.headerLogoType || undefined;
	config.headerLogoAltText = configuratorStore.headerLogoAltText || undefined;
	switch (configuratorStore.headerLogoType) {
		case ICON_TYPES.DOMNODE:
			config.headerLogo = utahHeader.renderDOM(configuratorStore.headerLogoDom);
			break;

		case ICON_TYPES.FUNCTION:
			config.headerLogo = useCodeSnippet(configuratorStore.headerLogoFunction);
			break;

		case ICON_TYPES.STRING:
			config.headerLogo = configuratorStore.headerLogoString;
			break;

		case ICON_TYPES.URL:
			config.headerLogo = configuratorStore.headerLogoUrl;
			break;

		default:
			configuratorStore.headerLogoType && console.error(`Unknown header logo type: ${configuratorStore.headerLogoType}`);
			break;
	}
};

const setUtahId = (configuratorStore, config) => {
	/*
		 * @param {object|boolean} utahId - Configuration for the UtahId, Sign In, Sign Out, etc. action item
		 * 		@param {string} utahId.environment - Environment from ENVIRONMENTS enum
		 * 		@param {string} utahId.customURL - A custom url for the UtahID web service api
		 * 		@param {string[]} utahId.customParams - An array of custom parameters to use for the UtahID api
		 * 		@param {function} utahId.authCallback - A function that is triggered when the users authentication changes
		 * @param {object} utahIdCustom - Configurations for a custom implementation of UtahID
		 * 		@param {function} utahIdCustom.onUserClickedSignIn - A function that is triggered when the user clicks to sign in
		 * 		@param {function} utahIdCustom.onUserClickedSignOut - A function that is triggered when the user clicks to sign out
		 * 		@param {string} utahIdCustom.profileURL - The URL of the users UtahID profile, this must be supplied for a profile menu item to be generated
	 */
	switch (configuratorStore.utahIdType) {
		case UtahIDTypes.none:
			break;

		case UtahIDTypes.custom:
			config.utahIdCustom = {
				onUserClickedSignIn: configuratorStore.utahIdCustom.onUserClickedSignIn ? useCodeSnippet(configuratorStore.utahIdCustom.onUserClickedSignIn) : undefined,
				onUserClickedSignOut: configuratorStore.utahIdCustom.onUserClickedSignOut ? useCodeSnippet(configuratorStore.utahIdCustom.onUserClickedSignOut) : undefined,
				profileURL: configuratorStore.utahIdCustom.profileURL,
			};
			break;

		case UtahIDTypes.plain:
			config.utahId = {
				environment: configuratorStore.utahId.environment,
				customURL: configuratorStore.utahId.environment === ENVIRONMENT.CUSTOM ? configuratorStore.utahId.customURL : undefined,
				customParams: (configuratorStore.utahId.customParams || '').split(' '),
				authCallback: configuratorStore.utahId.authCallback ? useCodeSnippet(configuratorStore.utahId.authCallback) : undefined,
			};
			break;

		case UtahIDTypes.on:
			config.utahId = true;
			break;

		default:
			console.error(`setUtahId(): unknown utahIdType: '${configuratorStore.utahIdType}'`);
			break;
	}
};

const setSearch = (configuratorStore, config) => {
	const isConfigured = configuratorStore.searchIconType !== IconTypes.none;

	if (isConfigured) {
		config.search = {
			showTitle: configuratorStore.searchShowTitle,
			title: configuratorStore.searchTitle,
		};

		if (configuratorStore.searchIconType === IconTypes.popupContent) {
			config.search.searchCallback = configuratorStore.searchCallback ? useCodeSnippet(configuratorStore.searchCallback) : undefined;
			config.search.placeholder = configuratorStore.searchPlaceholder;
		}

		if (configuratorStore.searchIconType === IconTypes.onClick) {
			config.search.onClick = configuratorStore.searchOnClick ? useCodeSnippet(configuratorStore.searchOnClick) : undefined;
		}
	}
};

export default configuratorStore => {
	const config = {
		headerShortTitle: configuratorStore.headerShortTitle || undefined,
		headerSize: configuratorStore.headerSize || undefined,
		headerStyle: configuratorStore.headerStyle || undefined,
		headerTitle: configuratorStore.headerTitle || undefined,
		headerTitleOnClick: configuratorStore.headerTitleOnClick ? useCodeSnippet(configuratorStore.headerTitleOnClick) : undefined,
		headerTitleURL: configuratorStore.headerTitleURL || undefined,

		modifierClasses: (configuratorStore.modifierClasses && configuratorStore.modifierClasses.length && [...configuratorStore.modifierClasses]) || undefined,

		onHeaderResize: configuratorStore.onHeaderResize ? useCodeSnippet(configuratorStore.onHeaderResize) : undefined,
	};

	setIcon('appGrid', configuratorStore, config);
	setIcon('gear', configuratorStore, config);
	setIcon('question', configuratorStore, config);
	setHeaderLogo(configuratorStore, config);
	setSearch(configuratorStore, config);
	setUtahId(configuratorStore, config);

	utahHeaderLoaded.then(() => window.utahHeader.setConfig(config));
};
